import { useCallback, useEffect, useRef, useState } from "react";

import { SCROLL_THUMB_TEST_ID } from "../constants";
import {
  getScrollLeftFromThumbDrag,
  getScrollLeftFromTrackClick,
  getScrollbarMetrics,
} from "../scrollbarUtils";
import { ScrollbarMetrics } from "../types";

const EMPTY_SCROLLBAR: ScrollbarMetrics = {
  visible: false,
  thumbWidth: 0,
  thumbOffset: 0,
  scrollPercentage: 0,
};

type UsePreviewSelectorScrollbarOptions = {
  itemsLength: number;
};

export const usePreviewSelectorScrollbar = ({
  itemsLength,
}: UsePreviewSelectorScrollbarOptions) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);

  const [scrollbar, setScrollbar] = useState<ScrollbarMetrics>(EMPTY_SCROLLBAR);

  const updateScrollbar = useCallback(() => {
    const scrollElement = scrollRef.current;
    const trackElement = trackRef.current;
    if (!scrollElement) {
      return;
    }

    const trackWidth = trackElement?.clientWidth ?? scrollElement.clientWidth;
    setScrollbar(getScrollbarMetrics(scrollElement, trackWidth));
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    updateScrollbar();

    scrollElement.addEventListener("scroll", updateScrollbar, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(scrollElement);
    if (trackRef.current) {
      resizeObserver.observe(trackRef.current);
    }

    return () => {
      scrollElement.removeEventListener("scroll", updateScrollbar);
      resizeObserver.disconnect();
    };
  }, [itemsLength, updateScrollbar]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) {
        return;
      }

      const canScrollHorizontally = scrollElement.scrollWidth > scrollElement.clientWidth;
      if (!canScrollHorizontally) {
        return;
      }

      const isPrimarilyVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!isPrimarilyVertical || event.deltaY === 0) {
        return;
      }

      event.preventDefault();
      scrollElement.scrollLeft += event.deltaY;
    };

    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    scrollElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      scrollElement.removeEventListener("wheel", handleWheel);
    };
  }, [itemsLength]);

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).dataset.testid === SCROLL_THUMB_TEST_ID) {
      return;
    }

    const scrollElement = scrollRef.current;
    const trackElement = trackRef.current;
    if (!scrollElement || !trackElement) {
      return;
    }

    const trackRect = trackElement.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;

    scrollElement.scrollLeft = getScrollLeftFromTrackClick(scrollElement, trackRect.width, clickX);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const scrollElement = scrollRef.current;
    const trackElement = trackRef.current;
    if (!scrollElement || !trackElement) {
      return;
    }

    const thumbElement = event.currentTarget;
    thumbElement.setPointerCapture(event.pointerId);

    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: scrollElement.scrollLeft,
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!dragStateRef.current || !scrollElement || !trackElement) {
        return;
      }

      const deltaX = moveEvent.clientX - dragStateRef.current.startX;
      scrollElement.scrollLeft = getScrollLeftFromThumbDrag(
        scrollElement,
        trackElement.clientWidth,
        dragStateRef.current.startScrollLeft,
        deltaX
      );
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      thumbElement.releasePointerCapture(event.pointerId);
      thumbElement.removeEventListener("pointermove", handlePointerMove);
      thumbElement.removeEventListener("pointerup", handlePointerUp);
      thumbElement.removeEventListener("pointercancel", handlePointerUp);
    };

    thumbElement.addEventListener("pointermove", handlePointerMove);
    thumbElement.addEventListener("pointerup", handlePointerUp);
    thumbElement.addEventListener("pointercancel", handlePointerUp);
  };

  return {
    scrollRef,
    trackRef,
    scrollbar,
    handleTrackPointerDown,
    handleThumbPointerDown,
  };
};
